import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { CalendarDays, Clock, Target, Flame, CheckCircle2, Trophy } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { getModuleIcon, MODULES, getModuleColors } from '../../../content';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { MS_PER_DAY } from '../../../config/constants';
import { getWeeklyActivityForLearner, getRecentSessionsForLearner } from '../../../utils/activityHelpers';
import { DataFreshness } from '../../shared/DataFreshness';
import { getChildLastActivity } from '../shared/childActivity';
interface ActivityPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  setViewingLearner: (id: string) => void;
}
export function ActivityPage({
  linkedLearners,
  selectedChild,
  childData,
  setViewingLearner
}: ActivityPageProps) {
  const {
    weeklyActivity,
    recentSessions,
    calendarWeeks,
    streakStats,
    weeklyStats
  } = useMemo(() => {
    if (!selectedChild || !childData) {
      return {
        weeklyActivity: [],
        recentSessions: [],
        calendarWeeks: [],
        streakStats: null,
        weeklyStats: {
          questions: 0,
          accuracy: 0,
          days: 0
        }
      };
    }
    const questionStates = childData.questionStates || {};
    // 1. Weekly Activity (current week)
    const weekly = getWeeklyActivityForLearner(questionStates);
    // 2. Recent Sessions
    const sessions = getRecentSessionsForLearner(questionStates, 7);
    // 3. Streak Stats — W4-5: slimmed from 4 cards to 2 (audit F-STAT-4).
    // Removed totalDailyGoalsReached + consecutiveFullWeeks: gamification
    // reporting with low parent action-value, mirrors the removed Prestaties-tab.
    const streakStats = {
      currentWeekDays: childData.dailyStreak?.currentWeekDays || 0,
      longestTotalStreak: childData.streak?.longestTotalStreak || 0
    };
    // 4. Weekly Summary Stats
    let questionsThisWeek = 0;
    let daysPracticedThisWeek = 0;
    weekly.forEach(day => {
      questionsThisWeek += day.questionCount;
      if (day.practiced) daysPracticedThisWeek++;
    });
    // Calculate accuracy for this week from recent sessions
    const now = Date.now();
    const oneWeekAgo = now - 7 * MS_PER_DAY;
    const sessionsThisWeek = sessions.filter(s => s.date >= oneWeekAgo);
    let totalQs = 0;
    let totalCorrect = 0;
    sessionsThisWeek.forEach(s => {
      totalQs += s.questionCount;
      totalCorrect += s.correctCount;
    });
    const accuracyThisWeek = totalQs > 0 ? Math.round(totalCorrect / totalQs * 100) : 0;
    // 5. 4-Week Calendar Grid
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const currentMonday = new Date(today);
    currentMonday.setDate(currentMonday.getDate() + mondayOffset);
    const weeks = [];
    for (let w = 3; w >= 0; w--) {
      const weekMonday = new Date(currentMonday);
      weekMonday.setDate(weekMonday.getDate() - w * 7);
      const weekMondayTs = weekMonday.getTime();
      const days = [];
      for (let d = 0; d < 7; d++) {
        const dayTs = weekMondayTs + d * MS_PER_DAY;
        const dayDate = new Date(dayTs);
        const isToday = dayTs === today.getTime();
        const isFuture = dayTs > today.getTime();
        // Count questions for this specific day
        let qCount = 0;
        for (const state of Object.values(questionStates)) {
          if (!state.history) continue;
          for (const attempt of state.history) {
            const attemptDate = new Date(attempt.timestamp);
            attemptDate.setHours(0, 0, 0, 0);
            if (attemptDate.getTime() === dayTs) {
              qCount++;
            }
          }
        }
        days.push({
          date: dayDate,
          isToday,
          isFuture,
          questionCount: qCount,
          practiced: qCount > 0
        });
      }
      // Label: e.g., "12 - 18 mrt"
      const startStr = `${weekMonday.getDate()} ${weekMonday.toLocaleString('nl-NL', {
        month: 'short'
      })}`;
      const endDay = new Date(weekMondayTs + 6 * MS_PER_DAY);
      const endStr = `${endDay.getDate()} ${endDay.toLocaleString('nl-NL', {
        month: 'short'
      })}`;
      weeks.push({
        label: w === 0 ? 'Deze week' : w === 1 ? 'Vorige week' : `${startStr} - ${endStr}`,
        days
      });
    }
    return {
      weeklyActivity: weekly,
      recentSessions: sessions,
      calendarWeeks: weeks,
      streakStats,
      weeklyStats: {
        questions: questionsThisWeek,
        accuracy: accuracyThisWeek,
        days: daysPracticedThisWeek
      }
    };
  }, [selectedChild, childData]);
  if (!selectedChild || !childData) {
    return <PageLayout title="Activiteit" description="Bekijk de oefenhistorie" data-id="element-7254">
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-4" data-id="element-7255">
          <DjayNarrator size="lg" emotion="thinking" data-id="element-7256" />
          <p className="text-slate-500 font-medium" data-id="element-7257">
            Selecteer een kind om de activiteit te bekijken.
          </p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Activiteit" description={`Bekijk wanneer en hoe ${selectedChild.displayName} oefent`} actions={<DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-7259" />} data-id="element-7258">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-7260">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-7261" />

        {/* Weekly Summary Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7262">
          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-7263">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-7264">
              <CalendarDays className="w-5 h-5 text-emerald-500" data-id="element-7265" />
            </div>
            <div data-id="element-7266">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-7267">
                Dagen geoefend
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-7268">
                {weeklyStats.days}{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-7269">
                  / 7 deze week
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-7270">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-7271">
              <Target className="w-5 h-5 text-blue-500" data-id="element-7272" />
            </div>
            <div data-id="element-7273">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-7274">
                Vragen beantwoord
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-7275">
                {weeklyStats.questions}{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-7276">
                  deze week
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-7277">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-7278">
              <CheckCircle2 className="w-5 h-5 text-purple-500" data-id="element-7279" />
            </div>
            <div data-id="element-7280">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-7281">
                Nauwkeurigheid
              </div>
              <div className="text-lg font-black text-slate-800" data-id="element-7282">
                {weeklyStats.accuracy}%{' '}
                <span className="text-sm font-bold text-slate-400" data-id="element-7283">
                  deze week
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-7284">
          {/* Left Column: Calendar & Streaks */}
          <div className="lg:col-span-2 space-y-8" data-id="element-7285">
            {/* 4-Week Calendar */}
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-7286">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7287">
                <CalendarDays className="w-4 h-4 text-emerald-500" data-id="element-7288" />
                Oefenkalender
              </h2>
              <Card className="p-6 bg-white border-slate-200 shadow-sm" data-id="element-7289">
                <div className="flex justify-between items-end mb-6" data-id="element-7290">
                  <div className="flex gap-4 text-xs font-medium text-slate-500" data-id="element-7291">
                    <div className="flex items-center gap-1.5" data-id="element-7292">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-7293" />{' '}
                      Geoefend
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-7294">
                      <div className="w-3 h-3 rounded-full bg-slate-100" data-id="element-7295" /> Niet
                      geoefend
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-7296">
                  {/* Day headers — F-ACT-2: responsive label column (44px mobile → 80px sm+) */}
                  <div className="grid grid-cols-[44px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] gap-1 sm:gap-2 mb-2" data-id="element-7297">
                    <div data-id="element-7298"></div>
                    {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => <div key={day} className="text-center text-xs font-bold text-slate-400" data-id="element-7299">
                        {day}
                      </div>)}
                  </div>

                  {/* Weeks */}
                  {calendarWeeks.map((week, i) => <div key={i} className="grid grid-cols-[44px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] gap-1 sm:gap-2 items-center" data-id="element-7300">
                      <div className="text-[10px] sm:text-xs font-medium text-slate-500 text-right pr-1 sm:pr-4 truncate" data-id="element-7301">
                        {week.label}
                      </div>
                      {week.days.map((day, j) => {
                    const dateLabel = day.date.toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short'
                    });
                    const ariaLabel = day.isFuture ? `${dateLabel}: in de toekomst` : day.practiced ? `${dateLabel}: ${day.questionCount} vragen geoefend${day.isToday ? ' (vandaag)' : ''}` : `${dateLabel}: niet geoefend${day.isToday ? ' (vandaag)' : ''}`;
                    return <div key={j} className="flex justify-center" data-id="element-7302">
                            <div role="img" aria-label={ariaLabel} className={`rounded-full transition-all ${day.isFuture ? 'w-6 h-6 bg-transparent border border-slate-100' : day.practiced ? day.isToday ? 'w-8 h-8 bg-emerald-500 shadow-md shadow-emerald-200 ring-2 ring-emerald-300 ring-offset-1' : 'w-6 h-6 bg-emerald-400' : day.isToday ? 'w-8 h-8 bg-slate-100 border-2 border-emerald-200 ring-2 ring-emerald-200 ring-offset-1' : 'w-6 h-6 bg-slate-100'} flex items-center justify-center`} title={day.isFuture ? '' : ariaLabel} data-id="element-7303">
                              {day.isToday && day.practiced && <CheckCircle2 className="w-4 h-4 text-white" data-id="element-7304" />}
                            </div>
                          </div>;
                  })}
                    </div>)}
                </div>
              </Card>
            </motion.section>

            {/* Streak Stats */}
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-7305">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7306">
                <Flame className="w-4 h-4 text-orange-500" data-id="element-7307" />
                Reeksen
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-7308">
                <Card className="p-6 bg-white border-slate-200 shadow-sm flex flex-col justify-center items-center text-center" data-id="element-7309">
                  <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-3" data-id="element-7310">
                    <Flame className="w-7 h-7 text-orange-500" data-id="element-7311" />
                  </div>
                  <div className="text-4xl font-black text-slate-800 mb-1" data-id="element-7312">
                    {streakStats?.currentWeekDays || 0}
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-7313">
                    Dagen op rij
                  </div>
                </Card>

                <Card className="p-6 bg-white border-slate-200 shadow-sm flex flex-col justify-center items-center text-center" data-id="element-7314">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3" data-id="element-7315">
                    <Trophy className="w-7 h-7 text-amber-500" data-id="element-7316" />
                  </div>
                  <div className="text-4xl font-black text-slate-800 mb-1" data-id="element-7317">
                    {streakStats?.longestTotalStreak || 0}
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-7318">
                    Langste reeks ooit
                  </div>
                </Card>
              </div>
            </motion.section>
          </div>

          {/* Right Column: Recent Sessions */}
          <div className="space-y-8" data-id="element-7319">
            <motion.section initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} data-id="element-7320">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7321">
                <Clock className="w-4 h-4 text-blue-500" data-id="element-7322" />
                Recente Sessies
              </h2>

              <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-7323">
                {recentSessions.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-7324">
                    {recentSessions.map((session, i) => {
                  const IconComp = getModuleIcon(session.moduleIcon);
                  const moduleData = session.moduleId ? MODULES.find(m => m.id === session.moduleId) : null;
                  const colors = getModuleColors(moduleData?.color || 'text-slate-500');
                  const date = new Date(session.date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const dateStr = isToday ? 'Vandaag' : date.toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'short'
                  });
                  const timeStr = date.toLocaleTimeString('nl-NL', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  return <div key={i} className="p-4 hover:bg-slate-50 transition-colors" data-id="element-7325">
                          <div className="flex items-center justify-between mb-3" data-id="element-7326">
                            <div className="text-xs font-bold text-slate-400" data-id="element-7327">
                              {dateStr} om {timeStr}
                            </div>
                            <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${session.accuracy >= 80 ? 'bg-emerald-100 text-emerald-700' : session.accuracy >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-7328">
                              {session.accuracy}% goed
                            </div>
                          </div>

                          <div className="flex items-center gap-3" data-id="element-7329">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.light} ${colors.text}`} data-id="element-7330">
                              <IconComp className="w-5 h-5" data-id="element-7331" />
                            </div>
                            <div className="min-w-0" data-id="element-7332">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-7333">
                                {session.moduleTitle}
                              </div>
                              <div className="text-xs font-medium text-slate-500" data-id="element-7334">
                                {session.questionCount} vragen geoefend
                              </div>
                            </div>
                          </div>
                        </div>;
                })}
                  </div> : <div className="p-8 text-center" data-id="element-7335">
                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-7336">
                      <Clock className="w-6 h-6 text-slate-300" data-id="element-7337" />
                    </div>
                    <p className="text-sm font-medium text-slate-500" data-id="element-7338">
                      Nog geen recente sessies.
                    </p>
                  </div>}
              </Card>
            </motion.section>
          </div>
        </div>
      </div>
    </PageLayout>;
}